import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Landmark } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_LABELS = {
  ADJUDICACION_INMUEBLE: 'Adjudicación Inmueble',
  COMPRA_CREDITO: 'Compra de Crédito',
};

const TIPO_DESCRIPCIONES = {
  ADJUDICACION_INMUEBLE: 'Aplica a Compra directa, Subasta, Cesión de remate, Ocupado y REO. Tributación: ITP o IVA (excluyentes).',
  COMPRA_CREDITO: 'Aplica a Compra de crédito. Tributación: AJD.',
};

const TIPO_ORDER = ['ADJUDICACION_INMUEBLE', 'COMPRA_CREDITO'];

// Virtual label → concrete backend tipos_operativos it synchronises.
const SYNCED_TIPOS = {
  ADJUDICACION_INMUEBLE: ['COMPRA_DIRECTA', 'SUBASTA', 'REMATE', 'OCUPADO', 'REO'],
  COMPRA_CREDITO: ['CREDITO'],
};

// Primary tipo whose values drive the UI for each virtual row (used for reading only).
const PRIMARY_TIPO = {
  ADJUDICACION_INMUEBLE: 'COMPRA_DIRECTA',
  COMPRA_CREDITO: 'CREDITO',
};

// All tax options are selectable for any tipo. CREDITO defaults to AJD but the
// user can change it if they wish.
const ALL_IMPUESTOS = ['ITP', 'AJD', 'IVA'];

export const TaxRulesSettingsSection = () => {
  const [rules, setRules] = useState(null);
  const [baseLabels, setBaseLabels] = useState({});
  const [sourcesByTipo, setSourcesByTipo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/tax-rules`, { withCredentials: true });
      setRules(res.data.rules);
      setBaseLabels(res.data.base_labels || {});
      setSourcesByTipo(res.data.sources_by_tipo || {});
    } catch { toast.error('Error al cargar reglas de impuestos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRule = (virtualKey, field, value) => {
    setRules(prev => {
      const next = { ...prev };
      const targets = SYNCED_TIPOS[virtualKey] || [virtualKey];
      targets.forEach(t => {
        next[t] = { ...(prev[t] || {}), [field]: value };
      });
      return next;
    });
  };

  const updateIva = (field, value) => {
    setRules(prev => ({ ...prev, iva: { ...(prev.iva || {}), [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/tax-rules`, rules, { withCredentials: true });
      toast.success('Reglas de impuestos guardadas');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Restaurar las reglas de impuestos a los valores por defecto?')) return;
    setLoading(true);
    try {
      await axios.post(`${API}/settings/tax-rules/reset`, {}, { withCredentials: true });
      await load();
      toast.success('Reglas restauradas');
    } catch { toast.error('Error al restaurar'); }
  };

  if (loading || !rules) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tax-rules-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><Landmark className="w-5 h-5 text-blue-600" /> Reglas de Impuestos</h2>
          <p className="body-text mt-1">Define por tipo de operación qué impuesto aplica y cómo se calcula la base imponible. Usado por el botón <em>Calcular impuestos</em> del Análisis ROI y por el Scoring Crédito.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleReset} className="btn-secondary text-sm flex items-center gap-2" data-testid="tax-rules-reset-btn">
            <RotateCcw className="w-4 h-4" /> Restaurar defecto
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="tax-rules-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar reglas'}
          </button>
        </div>
      </div>

      {/* Per-tipo rules */}
      <div className="card-base p-5">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Por tipo de operación</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase border-b border-slate-200">
                <th className="text-left py-2 px-3">Tipo operativo</th>
                <th className="text-left py-2 px-3">Impuesto principal</th>
                <th className="text-left py-2 px-3">Base imponible (por defecto)</th>
                <th className="text-right py-2 px-3">Tipo % (por defecto)</th>
              </tr>
            </thead>
            <tbody>
              {TIPO_ORDER.map(virtualKey => {
                const primary = PRIMARY_TIPO[virtualKey];
                const rule = rules[primary] || {};
                const sources = sourcesByTipo[primary] || [];
                return (
                  <tr key={virtualKey} className="border-b border-slate-100" data-testid={`tax-rule-${virtualKey}`}>
                    <td className="py-2.5 px-3 align-top">
                      <div className="font-medium text-slate-800">{TIPO_LABELS[virtualKey]}</div>
                      <p className="text-[11px] text-slate-400 mt-0.5 max-w-xs">{TIPO_DESCRIPCIONES[virtualKey]}</p>
                    </td>
                    <td className="py-2.5 px-3">
                      <select
                        className="input-base text-sm py-1 w-24"
                        value={rule.impuesto || ALL_IMPUESTOS[0]}
                        onChange={e => updateRule(virtualKey, 'impuesto', e.target.value)}
                        data-testid={`tax-rule-${virtualKey}-impuesto`}
                      >
                        {ALL_IMPUESTOS.map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                      {virtualKey === 'ADJUDICACION_INMUEBLE' && (
                        <p className="text-[11px] text-slate-400 mt-1">IVA y ITP son excluyentes a nivel del activo.</p>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <select
                        className="input-base text-sm py-1 w-full max-w-sm"
                        value={rule.base_fuente || (sources[0] || '')}
                        onChange={e => updateRule(virtualKey, 'base_fuente', e.target.value)}
                        data-testid={`tax-rule-${virtualKey}-base`}
                      >
                        {sources.map(s => (
                          <option key={s} value={s}>{baseLabels[s] || s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <input
                          type="number"
                          step="0.1"
                          className="input-base text-sm py-1 w-20 text-right"
                          value={rule.tipo_default ?? 0}
                          onChange={e => updateRule(virtualKey, 'tipo_default', parseFloat(e.target.value) || 0)}
                          data-testid={`tax-rule-${virtualKey}-tipo-default`}
                        />
                        <span className="text-xs text-slate-500">%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* IVA block */}
      <div className="card-base p-5" data-testid="tax-rules-iva">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">IVA (alternativo a ITP)</h3>
        <p className="text-xs text-slate-500 mb-4">
          Cualquier activo con ITP puede alternativamente tributar por IVA. No se aplican simultáneamente.
          La base imponible del IVA sigue la misma regla que la de ITP para ese tipo operativo.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Tipos permitidos (separados por coma)</label>
            <input
              type="text"
              className="input-base"
              value={(rules.iva?.tipos_permitidos || []).join(', ')}
              onChange={e => {
                const tokens = e.target.value.split(',').map(t => parseFloat(t.trim())).filter(n => !isNaN(n));
                updateIva('tipos_permitidos', tokens);
              }}
              placeholder="21, 10, 0"
              data-testid="iva-tipos-permitidos"
            />
            <p className="text-[11px] text-slate-400">Se muestran en el desplegable del activo.</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Tipo por defecto</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.1"
                className="input-base text-right"
                value={rules.iva?.tipo_default ?? 21}
                onChange={e => updateIva('tipo_default', parseFloat(e.target.value) || 0)}
                data-testid="iva-tipo-default"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Etiqueta tipo 0%</label>
            <input
              type="text"
              className="input-base"
              value={rules.iva?.etiqueta_0 || ''}
              onChange={e => updateIva('etiqueta_0', e.target.value)}
              placeholder="0% (Inversión Sujeto Pasivo)"
              data-testid="iva-etiqueta-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxRulesSettingsSection;
